/**
 * Help Center: "changes-cancellations" section articles. Format and rules: src/content/help/README.md.
 */
export default [
  {
    slug: 'change-your-flight',
    title: 'How to change your flight',
    summary: 'Move your trip to new dates or times on the same route with Change Flight, in your trip’s Flight Options. You choose the new flight and confirm it yourself.',
    keywords: ['reschedule', 'new dates', 'different time', 'rebook', 'modify booking', 'move my trip', 'Flight Options'],
    related: ['changes-cancellations/why-cant-i-change', 'changes-cancellations/change-dates-or-times', 'changes-cancellations/change-airport-or-destination', 'travel-insurance/insurance-and-flight-changes'],
    updated: '2026-10-05',
    body: `You can move your trip to a new date or time with **Change Flight**, in your trip's **Flight Options** menu. You choose the new flight and confirm the change yourself, so there's no request to send and nothing to wait for.

A flight change on Defera keeps your route the same. You fly between the same two places, on different flights. If you need something else, like a different airport, there's another path, and we explain it below.

## How do I change my flight?

Here's what happens:

1. **Open your trip.** Go to **Trips** and tap the trip you want to change.
2. **Open the menu.** Tap **Flight Options**, the menu at the top right of the **Flight Details** section.
3. **Start the change.** Tap **Change Flight**.
4. **Search for your new flight.** The search keeps your trip's origin and destination, so you're choosing new dates or times.
5. **Review and confirm.** Check the new flight, then confirm the change. Nothing is filed, and nobody needs to process it.

## What counts as a flight change?

A flight change means new dates or times between the two places you booked. Two kinds of requests work differently:

- **A different departure airport or destination.** The route of a booked trip can't be changed, so you'd cancel this trip and book a new one.
- **A departure later than your original date.** That's handled as a cancellation and a new booking, not a flight change.

In both cases, your cancel screen shows what you'd get back before you decide. If you'd like to fly later, [Changing your travel dates or times](/help/changes-cancellations/change-dates-or-times) walks you through your options.

## What if I see Request Flight Change instead?

**Change Flight** appears when your fare is eligible: fares other than Basic Economy, on {{CHANGE_FLIGHT_AIRLINES}}. On other fares and airlines, **Flight Options** shows **Request Flight Change** in its place.

Tapping **Request Flight Change** opens a chat with the Defera Concierge, our AI concierge, with your request already written. The concierge sends it to our team. Your request shows as a card on your trip page, and the answer arrives there and by email, usually within 24 hours.

The scope is the same as **Change Flight**: new dates or times on your existing route. [Why can't I change my flight?](/help/changes-cancellations/why-cant-i-change) covers the other reasons you might not see **Change Flight**.

## What if my trip is paid in full?

Once your trip is paid in full and your confirmation number has been released, date and time changes are made directly with the airline. Your confirmation number is the code the airline uses to find your reservation. You give it to the airline, and the airline's fare rules decide what's possible.

If you tap **Change Flight** on a trip like this, you'll see the same guidance. If the airline asks you to contact the company you booked through, contact us and our team will take it from there.

## What about older plans?

On some older Defera Installments plans, **Change Flight** opens a prompt to message us instead of a search. The Defera Concierge takes your request in chat and sends it to our team.

## Will a change cost anything?

That depends on your fare. What a change means for your price is shown in the app before you confirm, so you can decide with the full picture. Because it differs from fare to fare, we don't list change prices here.

> [!TIP] Not sure whether to change or cancel? Chat with the [Defera Concierge](/support) first. It can explain the options for your trip.`,
  },
  {
    slug: 'why-cant-i-change',
    title: 'Why can’t I change my flight?',
    summary: 'Change Flight isn’t offered on every fare or airline. New routes and later dates mean cancelling and rebooking, and paid-in-full trips change with the airline.',
    keywords: ['basic economy', 'greyed out', 'not eligible', 'missing button', 'restrictive fare', 'Request Flight Change', 'not allowed'],
    related: ['changes-cancellations/change-your-flight', 'changes-cancellations/change-airport-or-destination', 'confirmation-and-travel/airline-says-contact-third-party', 'missed-payments/what-is-default'],
    updated: '2026-10-05',
    body: `If you can't change your flight in the app, it's usually for one of a few reasons. Your fare or airline may not support self-serve changes, the change you want may be a new route or a later date, or your trip may already be paid in full.

None of these means something is wrong with your booking. Each one has a clear next step, and this article walks you through them.

## Which situation am I in?

This table matches each situation with what it means:

| Your situation | What it means |
|---|---|
| You see **Request Flight Change** instead of **Change Flight** | Your fare or airline isn't eligible for self-serve changes |
| You want to fly from or to a different place | A booked trip's route can't be changed |
| You want to depart later than your original date | That's a cancellation and a new booking |
| Your trip is paid in full, with a confirmation number | Changes are made directly with the airline |
| **Flight Options** is greyed out | The trip is cancelled, in payment default, or every flight on it has departed |

Whichever it is, there's still a way forward. The sections below explain each one.

## Why do I see Request Flight Change?

**Change Flight** is offered on fares other than Basic Economy, on {{CHANGE_FLIGHT_AIRLINES}}. If your fare is Basic Economy, or your airline isn't on that list, **Flight Options** shows **Request Flight Change** instead.

Here's what happens when you tap it:

1. **A chat opens.** It's the Defera Concierge, our AI concierge, with your request already written.
2. **You add the details.** Tell the concierge the dates or times you'd like.
3. **The concierge sends your request to our team.** It shows as a card on your trip page.
4. **You get an answer.** It appears on your trip page and arrives by email, usually within 24 hours.

The scope is the same as **Change Flight**: new dates or times on your existing route.

## Why can't I change my route?

The route of a booked trip can't be changed on Defera. That's true in the app, and through a request to our team too. To fly from a different airport or to a different place, you'd cancel this trip and book a new one.

[Changing your departure airport or destination](/help/changes-cancellations/change-airport-or-destination) explains how, and what you'd get back.

## Why can't I move my trip to a later date?

Departing later than your original departure date isn't a flight change on Defera. It's handled as a cancellation and a new booking.

Before you decide, open **Flight Options**, then **Cancel**, to see what cancelling would give you back. Opening that screen doesn't cancel anything by itself. [Changing your travel dates or times](/help/changes-cancellations/change-dates-or-times) walks you through it.

## What if my trip is paid in full?

Once your trip is paid in full and your confirmation number is released, date and time changes are made directly with the airline. You use your confirmation number, and the airline's fare rules decide what's possible. Tapping **Change Flight** shows the same guidance.

Some fares can't be changed at all. On restrictive fares, such as Basic Economy, Basic, Saver and Light, the airline doesn't accept changes or cancellations.

If the airline asks you to contact the company you booked through, contact us and our team will take it from there. [The airline told me to contact the third party](/help/confirmation-and-travel/airline-says-contact-third-party) explains what to do.

## Why is Flight Options greyed out?

The **Flight Options** menu is on every trip. It's greyed out only in three cases:

- the trip is cancelled;
- the trip is in payment default, which means its payment plan has ended and automatic payments have stopped;
- every flight on the trip has departed.

If none of those match your trip, contact us so we can take a look.

## What about older plans?

On some older Defera Installments plans, **Change Flight** opens a prompt to message us instead of a search. The Defera Concierge takes your request in chat and sends it to our team.

## Still not sure?

[Chat with the Defera Concierge](/support), our AI concierge. Tell it what you'd like to change, and it can explain the options for your trip.`,
  },
  {
    slug: 'change-dates-or-times',
    title: 'Changing your travel dates or times',
    summary: 'New dates or times on the same route are a flight change you make yourself with Change Flight. Departing later than your original date means cancelling and rebooking.',
    keywords: ['reschedule', 'postpone', 'different day', 'earlier flight', 'new departure date', 'push back my trip', 'Change Flight'],
    related: ['changes-cancellations/change-your-flight', 'changes-cancellations/cancel-your-trip', 'changes-cancellations/what-is-defera-credit', 'changes-cancellations/why-cant-i-change'],
    updated: '2026-10-05',
    body: `To fly on a different date or at a different time between the same two places, use **Change Flight** in your trip's **Flight Options**. You search for the new flight and confirm the change yourself.

There's one exception to know about. Departing later than the date you originally booked isn't a flight change on Defera. It's a cancellation and a new booking, and your cancel screen shows what you'd get back.

## How do I change my dates or times?

Here's how:

1. **Open your trip.** Go to **Trips** and tap your trip.
2. **Start the change.** Tap **Flight Options**, then **Change Flight**.
3. **Search for the flight you want.** Your origin and destination stay the same.
4. **Review and confirm.** Check the new flight, then confirm. The change is done, with no request to wait on.

If you see **Request Flight Change** instead, your fare or airline isn't eligible for self-serve changes. Tap it to send your request through the Defera Concierge, our AI concierge. [How to change your flight](/help/changes-cancellations/change-your-flight) has the details.

## What if I want to depart later?

Moving your departure past the date you booked works differently. You cancel this trip, then book your new dates as a new trip from [Book](/search).

For example, say your trip departs on May 10. Switching to a different flight on May 10 is a flight change. Moving your departure to May 17 means cancelling and booking again.

## What will I get back if I cancel to rebook?

That depends on your trip, so check your cancel screen before you decide. Open **Flight Options**, then **Cancel**. Opening the screen doesn't cancel anything by itself.

You'll see one of two things:

- **An offer of Defera Credit.** If your screen offers **Cancel & receive Defera Credit**, 100% of what you've paid comes back to your account instantly as Defera Credit. That's credit you can put toward any future trip on Defera, and it applies automatically when you book your new dates. Defera Credit is offered on select bookings, and eligibility may vary.
- **The standard cancellation options.** If your screen shows the standard options instead, cancelling isn't a free swap. Your trip's cancellation terms apply, and the screen shows what they are.

For example, if you've paid $300 so far and your screen offers Defera Credit, you'd have $300 of credit for your new dates. It goes to your new trip's deposit first, then to your upcoming payments.

[What you get back when you cancel](/help/changes-cancellations/what-you-get-back) explains each option in detail.

> [!IMPORTANT] Check your cancel screen before you cancel to rebook. Defera Credit never expires and can book any future trip, but it can't be turned into cash.

## What if my trip is paid in full?

Once your trip is paid in full and you have your confirmation number, date and time changes are made directly with the airline. You use your confirmation number, and the airline's fare rules decide what's possible.

## Not sure what to do?

Chat with the [Defera Concierge](/support), our AI concierge, before you change or cancel anything. It can explain the options for your trip.`,
  },
  {
    slug: 'change-airport-or-destination',
    title: 'Changing your departure airport or destination',
    summary: 'A booked trip’s route can’t be changed. To fly from or to somewhere else, you cancel and book a new trip, and your cancel screen shows what you’d get back.',
    keywords: ['different airport', 'new destination', 'change city', 'switch airports', 'route change', 'cancel and rebook', 'origin'],
    related: ['changes-cancellations/cancel-your-trip', 'changes-cancellations/what-you-get-back', 'changes-cancellations/use-defera-credit', 'booking/search-for-flights'],
    updated: '2026-10-05',
    body: `The route of a booked trip can't be changed on Defera. To fly from a different airport, or to a different place, you cancel this trip and book a new one.

We know that may not be the answer you hoped for. The good news is that your cancel screen shows exactly what you'd get back before you decide. On select bookings, that's everything you've paid, as Defera Credit for your new trip.

## Can your team change the route for me?

No. A flight change on Defera means new dates or times between the two places you booked. That's the same whether you use **Change Flight**, **Request Flight Change** or a request to our team. A different departure airport or destination always means a new booking.

## How do I cancel and rebook?

Here's what happens:

1. **Open your cancel screen.** Go to **Trips**, tap your trip, then tap **Flight Options** and **Cancel**.
2. **Read your options.** The screen shows what cancelling means for this trip. Opening it doesn't cancel anything by itself.
3. **Choose the option that fits.** The sections below explain what each one gives you.
4. **Book your new trip.** Search for your new route from [Book](/search).

## What if my screen offers Defera Credit?

On select bookings, the cancel screen says "This trip is eligible for Defera Credit" and offers **Cancel & receive Defera Credit**. Here's what that gives you:

- **Everything back, as credit.** 100% of what you've paid comes back to your account instantly, as Defera Credit.
- **No more payments.** Your payment plan stops right away.
- **Credit that applies itself.** When you book your new trip, the credit applies automatically at checkout, starting with the deposit.

For example, if you've paid $300 so far, you'd get $300 of Defera Credit to put toward your new trip.

Defera Credit is credit on your Defera account. It never expires, but it can't be turned into cash. It's offered on select bookings, and eligibility may vary.

## What if my screen shows the standard options?

If your cancel screen shows the standard options instead, cancelling to rebook isn't a free swap. Your trip's cancellation terms apply:

- **On Defera Installments,** cancelling ends the trip, not what you owe. Your remaining payments are still due.
- **On Defera Zero,** you can take 75% of what you've paid back to your card, up to 24 hours before departure. For example, if you've paid $400, that's $300 back to your card.

[What you get back when you cancel](/help/changes-cancellations/what-you-get-back) walks through each option.

## What if my trip is paid in full?

Once your trip is paid in full and you have your confirmation number, cancellation happens directly with the airline. You use your confirmation number, and the airline's fare rules apply. Fares are generally non-refundable. Some allow an airline flight credit, and only the airline can say what yours allows.

On Defera Zero, the 75% option stays on your cancel screen until 24 hours before departure.

> [!TIP] Not sure which way to go? Chat with the [Defera Concierge](/support), our AI concierge, before you cancel. It can explain the cancellation options for your trip.`,
  },
  {
    slug: 'cancel-your-trip',
    title: 'How to cancel your trip',
    summary: 'Open your trip, tap Flight Options, then Cancel. Your screen shows your trip’s exact options, and within 24 hours of booking your deposit comes back automatically.',
    keywords: ['cancellation', 'cancel booking', 'cancel flight', 'refund', 'cancellation policy', 'stop my trip', 'Flight Options'],
    related: ['changes-cancellations/what-you-get-back', 'changes-cancellations/cancel-within-24-hours', 'changes-cancellations/ask-for-an-exception', 'missed-payments/what-is-default'],
    updated: '2026-10-05',
    body: `To cancel, open your trip in **Trips**, tap **Flight Options**, then **Cancel**. The screen that opens shows the options for your trip, and it's the source of truth for what you'll get back.

What you see depends on your plan and your trip, so two people can see quite different screens. You don't need to work out your plan first, because your cancel screen already shows what applies to you. This article explains each version of the screen and what every button does.

## How do I cancel?

Here's what happens:

1. **Open your trip.** Go to **Trips** and tap the trip.
2. **Open the menu.** Tap **Flight Options**, the menu at the top right of the **Flight Details** section.
3. **Open your cancel screen.** Tap **Cancel**. This shows your options. It doesn't cancel anything by itself.
4. **Choose the option that fits.** Some options cancel your trip right there. Others open a chat with the Defera Concierge, our AI concierge, which sends your request to our team.

**Flight Options** is greyed out only when a trip is already cancelled, is in payment default, or every flight on it has departed.

## What if I booked in the last 24 hours?

If you cancel within 24 hours of booking, your deposit is refunded in full to the card you paid with, automatically. There's nothing to request and no reason to give. It's a refund to your card, not Defera Credit. [Cancelling within 24 hours of booking](/help/changes-cancellations/cancel-within-24-hours) explains when you'll see it.

## Which cancel screen will I see?

There are a few versions of the cancel screen. Find yours below. If your screen differs from what's described here, go by your screen.

### If your screen offers Defera Credit

On select bookings, on Defera Installments or Defera Zero, the screen is titled "Cancel Flight" and says "This trip is eligible for Defera Credit." A green box shows the amount you'll get back: 100% of everything you've paid so far.

- **Cancel & receive Defera Credit** cancels your trip and adds that amount to your Defera account instantly. Your payment plan stops right away, and no further payments are taken. If you haven't paid anything yet, the button reads **Cancel Flight** instead.
- **Prefer cash? Take 75% back instead** appears on Defera Zero trips, up to 24 hours before departure. It refunds 75% of your payments to your card, and shows you the exact amount first.

Defera Credit is credit on your Defera account that you can use toward any future trip, for yourself or someone else. It never expires. It's offered on select bookings, and eligibility may vary.

### Defera Zero, before your first payment

You'll see one button, **Cancel Trip**. It cancels your plan and confirms that nothing was charged. There's no request to send and nothing to wait for.

### Defera Zero, after your first payment

You'll see these options:

- **Cancel & receive 75% refund** returns 75% of your payments to your card, up to 24 hours before departure. It shows you the exact split and asks you to confirm, then the refund runs automatically.
- **Cancel & continue paying off balance** means you keep making your payments, and any flight credit your fare allows comes once your plan is paid in full. It opens a chat with the Defera Concierge, which sends your request to our team.
- **Cancel & stop payments** puts your trip into payment default. It also opens a chat with the concierge. We explain what it means further down this page.
- **Talk to support** opens a chat with the Defera Concierge for anything else.

### Defera Installments

You'll see **Cancel & continue paying off balance**, **Cancel & stop payments** and **Talk to support**. They work the same way as on Defera Zero. A cash refund isn't one of the options on this screen.

With these options, cancelling ends the trip, not what you owe. Your remaining payments are still due.

### Paid in full, with your confirmation number

Once your trip is paid in full and your confirmation number is released, cancellation happens directly with the airline. You use your confirmation number, and the airline's fare rules apply. Fares are generally non-refundable. Some allow an airline flight credit, and only the airline can say what yours allows.

On Defera Zero, **Cancel & receive 75% refund** stays on your screen until 24 hours before departure, so you can still choose it.

If the airline asks you to contact the company you booked through, contact us and our team will take it from there.

### Paid in full, waiting for your confirmation number

Chat with the [Defera Concierge](/support) to cancel. It sends your request to our team.

## What does Cancel & stop payments mean?

**Cancel & stop payments** doesn't end what you owe. It puts your trip into payment default. That means:

- what you've paid doesn't come back;
- your payment plan ends;
- the remaining balance is still owed.

Our [Terms of Service](/terms) set out what happens after a default.

## Is money tight right now?

If you're considering **Cancel & stop payments** because money is tight, please look at your other options first. Depending on your plan, you may be able to skip a payment or change your payment schedule. [I can't make my next payment. What are my options?](/help/missed-payments/cant-pay-on-time) explains each one.

## What else should I know?

- **Defera Miles you used on the trip** come back to your account automatically when it's cancelled.
- **A cancelled trip doesn't earn Miles,** because Miles are earned on completed trips.
- **If you booked with Miles alone,** the trip's **Cancel** button opens a chat with the Defera Concierge, which sends your request to our team. Your Miles come back automatically once it's cancelled.
- **Card refunds take time to show up.** They typically appear within 5–10 business days, depending on your bank. Defera Credit is on your account instantly.

## What if something serious has happened?

We're sorry. If you're cancelling because of an illness, a bereavement or another serious situation, you can ask our team to review it. [Asking for an exception to the cancellation policy](/help/changes-cancellations/ask-for-an-exception) explains how.

## Need help deciding?

[Chat with the Defera Concierge](/support), our AI concierge. **Talk to support**, where it appears on your cancel screen, opens the same chat.`,
  },
  {
    slug: 'what-you-get-back',
    title: 'What you get back when you cancel',
    summary: 'It depends on your trip. Eligible bookings get 100% back as Defera Credit, and Defera Zero can return 75% to your card up to 24 hours before departure.',
    keywords: ['refund', 'money back', 'cash back', '75 percent', 'flight credit', 'cancellation outcome', 'full refund'],
    related: ['changes-cancellations/cancel-your-trip', 'changes-cancellations/what-is-defera-credit', 'payments/refund-timing', 'travel-insurance/insurance-and-cancellations'],
    updated: '2026-10-05',
    body: `What you get back depends on your plan and your trip. Your cancel screen (**Flight Options**, then **Cancel**) shows your exact options and amounts before you choose anything.

Here's the big picture. On eligible bookings, you can get 100% of what you've paid back instantly as Defera Credit. With Defera Zero, you can take 75% of your payments back to your card instead, up to 24 hours before departure. And if you cancel within 24 hours of booking, your deposit goes back to your card automatically.

## What will I get back?

This table compares the main situations:

| Your situation | What you get back |
|---|---|
| You cancel within 24 hours of booking | Your deposit, in full, to your card, automatically |
| Your cancel screen offers Defera Credit | 100% of what you've paid, as Defera Credit, instantly |
| Defera Zero | 75% of your payments to your card, up to 24 hours before departure |
| Defera Installments, standard options | Any airline flight credit your fare allows, once your plan is paid in full |
| Paid in full, with your confirmation number | Whatever the airline's fare rules allow |

The quickest way to know which applies to you is to open your cancel screen. It shows your real options, and opening it doesn't cancel anything. Defera Credit is offered on select bookings, and eligibility may vary.

## How does Defera Credit work?

Defera Credit is credit on your Defera account. You can put it toward any future trip on Defera, for yourself or someone else. It posts instantly and never expires, but it can't be turned into cash.

If your screen offers **Cancel & receive Defera Credit**, you get back 100% of what you've paid, and your payment plan stops right away. [What is Defera Credit?](/help/changes-cancellations/what-is-defera-credit) tells you more.

## How does the Defera Zero 75% refund work?

On every Defera Zero trip, you can take 75% of your payments back to your card until 24 hours before departure. That includes trips that are already paid in full.

Here's what happens:

1. **Choose the cash option.** Tap **Cancel & receive 75% refund**. If your screen offers Defera Credit, tap **Prefer cash? Take 75% back instead**.
2. **Check the split.** The screen shows exactly how much comes back to your card.
3. **Confirm.** The refund then runs automatically. There's no request to send and nothing to wait on.

If your trip was paid entirely with Defera Credit, there's no card payment to refund. The screen says so, and cancelling returns all of it as Defera Credit.

## Should I choose credit or cash?

If your Defera Zero screen offers both, compare them before you confirm:

- **Defera Credit** is 100% of what you've paid, so it's worth more.
- **Cash** is 75% of your payments, back to your card.

For example, if you've paid $400 so far, Defera Credit would give you $400 toward a future trip. The cash option would return $300 to your card.

If you need the money, the cash option is yours to take. Choose carefully, though: once Defera Credit is on your account, it can't be turned into cash.

## What about Defera Installments?

If your cancel screen shows the standard options, cancelling ends the trip, not what you owe:

- **Cancel & continue paying off balance**: you keep making your payments. Any airline flight credit your fare allows comes after your plan is paid in full.
- **Cancel & stop payments**: nothing comes back. It's a payment default, and the remaining balance is still owed.

A cash refund isn't one of the options on this screen. If your situation doesn't fit them, you can ask our team to review it. [Asking for an exception to the cancellation policy](/help/changes-cancellations/ask-for-an-exception) explains how.

## Is a flight credit the same as Defera Credit?

No. An airline flight credit comes from the airline, under the airline's rules. Defera Credit comes from us. It lives on your Defera account and applies automatically when you book with us.

## What about restrictive fares?

On restrictive fares, such as Basic Economy, Basic, Saver and Light, the airline doesn't accept changes or cancellations. That means no refund or flight credit comes back from the airline. This applies to the standard options, and to trips that are paid in full with a confirmation number.

## What if my trip is paid in full?

Once you have your confirmation number, cancellation happens directly with the airline, under its fare rules, using your confirmation number. Fares are generally non-refundable. Some allow an airline flight credit, and only the airline can say what yours allows.

If the airline asks you to contact the company you booked through, contact us and our team will take it from there. On Defera Zero, the 75% option stays available until 24 hours before departure.

## When will I see my money?

- **Defera Credit** is on your account instantly.
- **Card refunds** leave us right away, but your bank decides when they appear: typically within 5–10 business days.
- **A deposit refunded within 24 hours of booking** usually drops off your statement within a day or so.

[When will my refund arrive?](/help/payments/refund-timing) has more on refund timing.

## What happens to my Miles?

Defera Miles you used on the trip come back to your account automatically. A cancelled trip doesn't earn Miles.`,
  },
  {
    slug: 'cancel-within-24-hours',
    title: 'Cancelling within 24 hours of booking',
    summary: 'Changed your mind? Cancel within 24 hours of booking and your deposit is refunded in full to your card, automatically, on every plan. No reason needed.',
    keywords: ['deposit refund', 'changed my mind', 'booked by mistake', 'cooling off period', 'cancel right after booking', 'refund to card'],
    related: ['changes-cancellations/cancel-your-trip', 'changes-cancellations/what-you-get-back', 'payments/refund-timing'],
    updated: '2026-10-23',
    body: `If you cancel within 24 hours of booking, your deposit is refunded in full to the card you paid with, automatically. You don't need to give a reason, and there's nothing to request.

This is our policy on every Defera plan, so changing your mind soon after booking is completely fine. It's a refund to your card, not Defera Credit.

## How long do I have?

You have 24 hours from when you booked. For example, if you book at 3 PM on a Tuesday, you have until 3 PM on Wednesday to cancel and get your deposit back automatically.

After that, your trip's standard cancellation options apply instead, and your cancel screen shows what they are. [What you get back when you cancel](/help/changes-cancellations/what-you-get-back) explains them.

## How do I cancel?

Here's what happens:

1. **Open your trip.** Go to **Trips** and tap the trip.
2. **Open your cancel screen.** Tap **Flight Options**, then **Cancel**.
3. **Choose the option that cancels your trip.** If you're not sure which one that is, tap **Talk to support** or chat with the [Defera Concierge](/support), our AI concierge, and say you booked within the last 24 hours.

You don't need to ask anyone for the deposit refund itself. Within 24 hours of booking, it's our policy, not a request, and your deposit comes back on its own.

## When will I see my money?

How it shows up depends on whether the charge has finished going through on your card:

- **If the charge is still pending,** it's reversed on your card. Most of the time it drops off your statement, usually within a day or so, rather than showing up as a separate refund. A pending charge is one your bank is holding but hasn't finished processing.
- **If the charge had already posted,** the money returns to your card within a few business days. Your bank controls exactly when it appears.

Either way, the deposit goes back to the card you paid with, not to your Defera account as credit.

## What does the 24-hour policy cover?

- **Your deposit, in full.** That's what the 24-hour policy refunds. Anything you paid beyond the deposit in those 24 hours follows your trip's normal cancellation outcome, such as Defera Credit on eligible bookings.
- **Defera Zero trips.** On Defera Zero, nothing is due at booking. If no payment has been taken yet, **Cancel Trip** cancels your plan with nothing charged.

## What if my deposit hasn't come back?

If you cancelled within 24 hours and the deposit still isn't back after a few business days, contact us so our team can check the transaction. Chat with the [Defera Concierge](/support), our AI concierge, or send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'what-is-defera-credit',
    title: 'What is Defera Credit?',
    summary: 'Defera Credit is credit on your Defera account that you can put toward any future trip, for you or anyone else. It never expires, but it can’t become cash.',
    keywords: ['account credit', 'trip credit', 'credit balance', 'cancellation credit', 'does it expire', 'voucher', 'house credit'],
    related: ['changes-cancellations/use-defera-credit', 'changes-cancellations/where-is-my-credit', 'changes-cancellations/credit-refund-or-transfer', 'booking/using-credit-at-checkout'],
    updated: '2026-10-05',
    body: `Defera Credit is credit on your Defera account that you can put toward any future trip on Defera, for yourself or for someone else. It posts to your account instantly and never expires.

Think of it as value you keep with us for your next trip. It isn't cash, so it can't be cashed out or refunded to a card. But it stays on your account for as long as you need it.

## How do I get Defera Credit?

You can get Defera Credit by cancelling an eligible trip. On select bookings, your cancel screen says "This trip is eligible for Defera Credit" and offers **Cancel & receive Defera Credit**. Here's what happens when you tap it:

1. **Your trip is cancelled.** Your payment plan stops right away, and no further payments are taken.
2. **Your credit arrives.** 100% of what you've paid lands on your account as Defera Credit, instantly.
3. **It's ready to use.** It applies automatically the next time you book.

Defera Credit is offered on select bookings, and eligibility may vary.

On select defaulted bookings, what you've paid can also be converted to Defera Credit. [Options for a defaulted trip](/help/missed-payments/defaulted-trip-options) explains how to ask.

## Where can I see it?

Open your avatar menu and choose **Profile**. Once you have Defera Credit, you'll see a **Defera Credits** card there. It shows your "Available credit", which is the amount you can spend now.

The card also keeps a history. It lists each time credit was added, applied to a trip, held for checkout or returned to your balance.

## What can I do with it?

- **Book any future trip on Defera.** It applies automatically at checkout, to your deposit first and then to your upcoming payments.
- **Choose how much to use.** Use all of it, some of it, or save it for another trip.
- **Book for someone else.** Your credit can pay for a trip for a partner, a parent or a friend.
- **Take your time.** It never expires, so it's there whenever you're ready.

For example, say you have $250 of Defera Credit and your next trip's deposit is $100. The credit covers the deposit, and the remaining $150 lowers your earliest payments on that trip.

[Using your Defera Credit](/help/changes-cancellations/use-defera-credit) shows how.

## What isn't Defera Credit?

- **It isn't cash.** Defera Credit can't be cashed out, refunded to a card or transferred to another company. [Can Defera Credit be refunded or transferred?](/help/changes-cancellations/credit-refund-or-transfer) covers the details.
- **It isn't an airline flight credit.** A flight credit comes from an airline, under that airline's rules. Defera Credit lives on your Defera account and applies automatically when you book with us.
- **It isn't Defera Miles.** Miles are your rewards balance, kept separately on the **Rewards** page.`,
  },
  {
    slug: 'use-defera-credit',
    title: 'Using your Defera Credit',
    summary: 'Your credit applies automatically when you book, deposit first. Got credit after booking a trip? Open that trip and tap Apply Credits in its payment schedule.',
    keywords: ['apply credit', 'redeem', 'spend', 'checkout', 'Apply Credits', 'deposit', 'book for someone else'],
    related: ['booking/using-credit-at-checkout', 'changes-cancellations/where-is-my-credit', 'changes-cancellations/what-is-defera-credit', 'booking/booking-for-others'],
    updated: '2026-10-05',
    body: `Your Defera Credit applies automatically when you book your next trip. It goes to your deposit first, then to your upcoming payments, and there's no code to enter.

If you received credit after booking a trip, you can still use it on that trip. Open the trip and tap **Apply Credits** in its payment schedule. You can use your credit on your own trips, or to book for someone else.

## How do I use it on a new trip?

Here's what happens:

1. **Find your trip.** Search and choose your flight from [Book](/search) as usual.
2. **Check the green panel.** On the review page, a green "Defera Credit applied" panel shows your credit and exactly what's due today.
3. **Choose how much to use, if you like.** Your full balance applies by default. To keep some for later, use the panel to set a lower amount. Set it to $0 and the panel reads "Not using your Defera Credit on this trip", leaving your whole balance on your account.
4. **Add your card and book.** Your card is saved for your future payments.

This works on Defera Installments and Defera Zero. Defera Zero has no deposit, so your credit goes to your payments, starting with the earliest.

For example, say you have $150 of Defera Credit and your new trip's deposit is $100. The credit covers the deposit, so your card is charged $0 today, and the remaining $50 lowers your first payment.

## Why does checkout still ask for my card?

Checkout asks for card details even when your credit covers the whole deposit. That isn't a charge. Your payment plan needs a card on file, which means a card saved to your plan for the payments ahead.

When credit covers everything due today, your card is charged $0 at booking. The "Due today" line shows what your card will actually be charged.

## What if my credit is more than the deposit?

The rest carries into your plan. It reduces your earliest payments, and anything left after that stays on your account for future payments and trips.

## How do I use credit on a trip I've already booked?

Credit that arrives after you book a trip, such as credit from cancelling a different trip, doesn't apply to that trip on its own. You add it yourself:

1. **Open the trip.** Go to **Trips** and tap the trip.
2. **Apply your credit.** In the trip's payment schedule, tap **Apply Credits**.
3. **See your new amounts.** Your credit is spread across your upcoming payments, starting with the next one.

If your next payment still shows the full amount, that's normal until you do this. The credit hasn't been applied to that trip yet.

When you pay off a trip that has Defera Credit applied, you're charged only what's left after the credit.

## Can I use it for someone else's trip?

Yes. Your credit can pay for a trip you book for someone else, such as a partner, a parent or a friend. Book the trip from your account, and the credit applies at checkout as usual. [Booking for other travelers](/help/booking/booking-for-others) explains how.

## How do I check my balance?

Open your avatar menu and choose **Profile**. The **Defera Credits** card shows your "Available credit" and its history. If it looks lower than you expected, see [Where's my Defera Credit?](/help/changes-cancellations/where-is-my-credit)`,
  },
  {
    slug: 'where-is-my-credit',
    title: 'Where’s my Defera Credit?',
    summary: 'Your credit is on your Profile, in the Defera Credits card. It posts instantly, so if it looks missing, it’s often already working on one of your trips.',
    keywords: ['missing', 'credit balance', 'not showing', 'not applied', 'Apply Credits', 'Available credit', 'cancellation credit', 'where did my refund go'],
    related: ['changes-cancellations/use-defera-credit', 'payments/payment-not-showing', 'rewards/how-miles-work', 'travel-insurance/insurance-and-cancellations'],
    updated: '2026-10-23',
    body: `Your Defera Credit is on your Profile, in the **Defera Credits** card. It shows your "Available credit" and a history of your credit.

Credit posts to your account instantly, so credit from a cancellation is there as soon as you cancel. If it seems to be missing, it's often already working on one of your trips, and the history shows where it went.

## Where do I find it?

Here's how:

1. **Open your Profile.** Tap your avatar menu, then **Profile**.
2. **Find the Defera Credits card.** It shows your "Available credit", the amount you can spend now.
3. **Check the history.** It lists each time credit was added, applied, held for checkout or returned to your balance.

The card appears once you have Defera Credit, whether that's a balance or any history. If you've never had credit, you won't see it.

You'll also see your credit at checkout on a new trip, in the green "Defera Credit applied" panel. Your booking confirmation email shows any Defera Credit used at checkout too.

## Why does my credit look missing?

There are four common reasons:

1. **It's already applied to a trip.** Credit you've applied to a trip leaves your "Available credit", because it's covering that trip's payments. Nothing is lost.
2. **It arrived after you booked.** Credit you receive after booking a trip doesn't reach that trip on its own. Open the trip in **Trips** and tap **Apply Credits** in the payment schedule.
3. **Your new trip cost more than your credit.** The trip total includes all fees, so if it was larger than your credit, the rest was due as usual.
4. **You're thinking of Defera Miles.** Miles are a separate rewards balance, on the [Rewards](/rewards) page.

For example, say you had $200 of credit and applied $120 of it to a trip. Your "Available credit" now shows $80. The other $120 is still yours, working on that trip's payments, and the history shows it as applied.

## When should I contact you?

Contact us so our team can check your account if:

- you cancelled for Defera Credit and it never arrived; or
- your card was charged the full deposit even though you had credit.

Chat with the [Defera Concierge](/support), our AI concierge, or send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'credit-refund-or-transfer',
    title: 'Can Defera Credit be refunded or transferred?',
    summary: 'Defera Credit can’t be cashed out, refunded or moved to another company. But you can use it to book a trip for anyone, and it never expires.',
    keywords: ['cash out', 'convert to cash', 'money back', 'give credit to someone', 'share credit', 'bank transfer', 'refund to card'],
    related: ['changes-cancellations/what-is-defera-credit', 'changes-cancellations/use-defera-credit', 'changes-cancellations/what-you-get-back', 'missed-payments/cant-pay-on-time'],
    updated: '2026-10-05',
    body: `Defera Credit can't be refunded or turned into cash, and it can't be transferred to another company. You can still share it, though. It can pay for a trip you book for anyone, and it never expires.

We know this question often comes up when money is tight, and we're sorry the answer isn't different. Below, we explain what your credit can do, and the options that may help if your situation has changed.

## What can't Defera Credit do?

Defera Credit is for travel on Defera, so it can't be:

- cashed out, or refunded to a card;
- sent by bank transfer, PayPal, Venmo or Cash App, or mailed as a check;
- transferred to another company;
- exchanged for money in any other way, in full or in part.

This is the same for everyone. There's no approval or exception process for it, so it isn't something our team can review. We'd rather tell you that clearly now than leave you waiting on a request that can't change the answer.

## What can it do?

- **Book any future trip on Defera.** It applies automatically at checkout, deposit first.
- **Pay for someone else's trip.** Book a trip for a partner, a parent or a friend from your account, and your credit applies as usual. You don't need to transfer it to share it.
- **Wait for you.** It never expires, so it'll be there whenever you're ready.

For example, if you can't travel this year, you could keep your credit for a trip next year, or use it now to book a flight for a family member.

[Using your Defera Credit](/help/changes-cancellations/use-defera-credit) shows how.

## Can I get cash instead when I cancel?

That's a different question. The rule above is about Defera Credit that's already on your account. Whether you get cash or credit when you cancel a trip depends on your trip, and your cancel screen shows your options:

- **Defera Zero:** you can take 75% of your payments back to your card, up to 24 hours before departure. If your screen also offers Defera Credit, this option appears as **Prefer cash? Take 75% back instead**.
- **Defera Installments:** a cash refund isn't one of the options on the cancel screen. If your situation doesn't fit the options shown, you can ask our team to review it before you cancel.

[Asking for an exception to the cancellation policy](/help/changes-cancellations/ask-for-an-exception) explains how to ask.

## What if money is tight right now?

If a current trip's payments are the worry, there may be options that help, depending on your plan. [I can't make my next payment. What are my options?](/help/missed-payments/cant-pay-on-time) covers them.

## Still have a question?

Chat with the [Defera Concierge](/support), our AI concierge, about anything else to do with your credit.`,
  },
  {
    slug: 'ask-for-an-exception',
    title: 'Asking for an exception to the cancellation policy',
    summary: 'If illness, a bereavement or another serious situation means your cancel options don’t fit, the Defera Concierge can send your request to our team to review.',
    keywords: ['medical emergency', 'bereavement', 'death in the family', 'illness', 'hospital', 'hardship', 'special circumstances', 'refund request'],
    related: ['changes-cancellations/cancel-your-trip', 'changes-cancellations/credit-refund-or-transfer', 'contact/how-requests-work', 'contact/defera-concierge'],
    updated: '2026-10-23',
    body: `We're sorry you're dealing with something difficult. If an illness, a medical emergency, a death in the family or another serious situation means the options on your cancel screen don't fit, you can ask our team to review your situation.

To ask, start a chat with the Defera Concierge, our AI concierge. It sends your request to our team, and a person on our team reviews it. You don't need to have everything worked out before you reach out.

## How do I ask?

Here's what happens:

1. **Open a chat.** Start a chat with the [Defera Concierge](/support). If your cancel screen shows **Talk to support**, it opens the same chat.
2. **Tell the concierge what's happened.** Say which trip it's about, what has happened in a sentence or two, and what you're hoping for.
3. **The concierge sends your request.** It goes to our team and shows as a card on your trip page.
4. **Our team replies.** The answer appears on your trip page and arrives by email, usually within 24 hours.

If your flight is coming up soon, say so at the start of the chat, so the concierge can send it as an urgent request.

## Can I call or email instead?

Yes. Call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}) to reach the same concierge by voice, 24/7. It's the same AI concierge as the chat, so use whichever feels easier. You can also send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.

## What should I include?

- **Your booking reference.** It's the code at the top of your booking confirmation email, and it's on your trip page too.
- **What has happened, briefly.** You don't need to share medical details or a diagnosis in chat.
- **What you'd like to happen.** For example, cancelling the trip.

If our team needs documentation, they'll ask you for it.

## What happens next?

- Your request shows on your trip page as "Submitted · in progress" while our team works on it.
- Our team's answer appears on your trip page and arrives by email.
- We can't tell you in advance what our team will decide, because every situation is different.

## Is there anything I can do myself?

- **If an option on your cancel screen already works for you,** you can use it yourself. [How to cancel your trip](/help/changes-cancellations/cancel-your-trip) explains each one.
- **On Defera Zero,** you can take 75% of your payments back to your card from the cancel screen, up to 24 hours before departure.
- **If you booked within the last 24 hours,** cancelling gets your deposit refunded in full to your card, automatically.
- **Defera Credit already on your account** can't be turned into cash, and that isn't something our team can make an exception for.`,
  },
  {
    slug: 'remove-a-traveler',
    title: 'Removing a traveler from your trip',
    summary: 'To take a traveler off a booked trip, ask the Defera Concierge. It sends your request to our team, and the answer comes to your trip page and by email.',
    keywords: ['remove passenger', 'drop a passenger', 'take someone off', 'fewer travelers', 'passenger can’t go', 'cancel one person'],
    related: ['changes-cancellations/correct-traveler-details', 'changes-cancellations/cancel-your-trip', 'contact/how-requests-work'],
    updated: '2026-10-23',
    body: `To take a traveler off a booked trip, ask us. Chat with the Defera Concierge, our AI concierge, and it sends your request to our team. The answer appears on your trip page and arrives by email.

Please send the request before you cancel or change anything else on the trip. Cancelling the trip for everyone is a separate decision, with its own terms, and you may not need to make it.

## How do I ask?

Here's what happens:

1. **Start a chat.** Open a chat with the [Defera Concierge](/support).
2. **Tell it who can't travel.** Say which trip and which traveler, and that they can no longer travel.
3. **The concierge sends your request.** It goes to our team and shows as a card on your trip page, marked "Submitted · in progress".
4. **Our team replies.** The answer appears on that card and arrives by email, usually within 24 hours.

## Can I call or email instead?

Yes. Call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}) to reach the same concierge by voice, 24/7. You can also send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.

## What should I include?

So our team has what it needs from the start, include:

- **Your booking reference.** It's the code at the top of your booking confirmation email, and it's on your trip page too.
- **The traveler's full name,** as it appears on the booking.
- **Whether everyone else on the booking is still traveling.**
- **Any questions you have** about your trip total or your payments.

## What happens to my payments while I wait?

Nothing changes on your trip until our team replies. Your payment schedule stays as it is in the meantime.

Please wait for our team's answer before assuming any change to your trip total or your payments. We can't say in advance what will change for a particular booking.

## What if someone else will travel instead?

If a different person would take the traveler's place, ask the same way. Include the new traveler's full name exactly as it appears on their government ID. [Fixing a name or date of birth on your booking](/help/changes-cancellations/correct-traveler-details) covers what else to include.

## What if no one on the trip can go?

Cancelling the trip for everyone is a different decision, with its own terms. If you're considering it, check your cancel screen first: open your trip, tap **Flight Options**, then **Cancel**. Opening the screen doesn't cancel anything by itself. [What you get back when you cancel](/help/changes-cancellations/what-you-get-back) explains the options.`,
  },
  {
    slug: 'still-taking-this-trip',
    title: 'The "Will you still be taking this trip?" email',
    summary: 'A courtesy check-in, not a warning. Still going? There’s nothing to do. Plans changed? Check your cancel screen soon: the Defera Credit option can close without notice.',
    keywords: ['two month email', 'check-in email', 'still traveling', 'is this email real', 'plans changed', 'courtesy email', 'trip check'],
    related: ['changes-cancellations/what-is-defera-credit', 'changes-cancellations/cancel-your-trip', 'emails/emails-we-send', 'emails/is-this-email-from-defera'],
    updated: '2026-10-05',
    body: `This email is a real, automatic check-in from Defera, and it isn't a warning. If you're still taking your trip, there's nothing to do. Your payment plan keeps running exactly as scheduled.

If your plans have changed, the email lets you know you can cancel and get 100% of what you've paid back as Defera Credit. That option doesn't last until departure, so it's worth deciding soon.

## Why did I get it?

We send it once per trip, when departure is about two months away, to customers who booked more than two months ahead. For example, if you booked in January for a flight on June 20, it would arrive around April 20.

Nothing is wrong with your booking. No payment was missed, and nobody is cancelling anything. It isn't a nudge to cancel either. We'd rather ask than let a good option quietly close on you.

If yours arrived closer to your trip, it still applies to you.

## I'm still going. Do I need to do anything?

No. You don't need to reply or do anything. Your trip and your payment plan carry on exactly as scheduled.

## My plans have changed. What should I do?

Here's what happens:

1. **Open your trip.** Go to **Trips** and tap the trip.
2. **Open your cancel screen.** Tap **Flight Options**, then **Cancel**. Opening it doesn't cancel anything by itself.
3. **Check the amount.** If the screen offers **Cancel & receive Defera Credit**, it shows the amount you'll get back: 100% of what you've paid.
4. **Cancel for credit.** Tap **Cancel & receive Defera Credit**. The credit is on your account instantly.

Defera Credit is credit on your Defera account. It never expires, and it can book any future trip, including trips for other people. It can't be turned into cash.

On Defera Zero, the same screen also offers 75% of your payments back to your card instead, up to 24 hours before departure.

## How long is the Defera Credit option available?

We can't give you a date. The option doesn't last until departure, and it can close without notice. Deciding now is the only way to be sure of it. Defera Credit is offered on select bookings, and eligibility may vary.

## What if my cancel screen doesn't offer Defera Credit?

Your cancel screen shows your trip's options as they stand today. If it no longer offers Defera Credit, it shows your trip's current cancellation terms instead. [What you get back when you cancel](/help/changes-cancellations/what-you-get-back) explains them.

## I didn't get this email. Is something wrong?

No. Not every trip receives it, and not getting it doesn't mean anything is wrong. Your cancel screen shows your trip's current options.

## Can I reply to it?

Yes. Your reply reaches our team. You can also chat with the [Defera Concierge](/support), our AI concierge, about your trip.`,
  },
  {
    slug: 'airline-schedule-change',
    title: 'If the airline changes or cancels your flight',
    summary: 'You didn’t do anything wrong. Tell us as soon as you hear, with your booking reference and what the airline said, and we’ll help you navigate it.',
    keywords: ['schedule change', 'delay', 'new departure time', 'disruption', 'rebooked', 'DOT', 'EU261'],
    related: ['contact/urgent-help-before-a-flight', 'contact/how-requests-work', 'booking/codeshare-flights', 'booking/free-flight-upgrade-question'],
    updated: '2026-10-23',
    body: `You didn't do anything wrong: changes like this come from the airline. Contact us as soon as you hear about it, and we'll help you navigate it.

We know a cancelled or changed flight can be stressful, especially when your trip is close. Airline cancellations and delays are handled by the airline operating the flight, under the rules that apply, such as US Department of Transportation (DOT) rules or EU261 in Europe. You don't have to sort it out alone.

## How do I let you know?

Choose whichever is easiest for you:

- **Chat** with the [Defera Concierge](/support), our AI concierge. It sends your request to our team, and the answer appears on your trip page and arrives by email.
- **Call** [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}) to reach the same concierge by voice, 24/7.
- **Send us a message** from the [Contact page](/contact#message). Our team replies within 24 hours.

Calling isn't a faster lane than chatting. It's the same AI concierge, by voice.

## What if my flight is very soon?

Say so at the start of your chat, and tell the concierge when you fly. It can send an urgent request to our team, which is the fastest way to reach a person. [Getting urgent help before your flight](/help/contact/urgent-help-before-a-flight) has more.

## What should I include?

So our team has what it needs from the start, include:

- **Your booking reference.** It's the code at the top of your booking confirmation email, and it's on your trip page too.
- **Which flight changed.** Tell us what the airline told you: the new times, or that the flight is cancelled.
- **Whether the new schedule works for you.**
- **The airline's email or text,** if you have one, so the details are exact.

## What happens next?

Here's what happens:

1. **You tell the concierge.** Share what the airline told you.
2. **The concierge sends a request to our team.** It shows as a card on your trip page, marked "Submitted · in progress".
3. **Our team replies.** They look at your trip, and the answer appears on your trip page and arrives by email.

Every airline change is different, so we don't list outcomes here.

## Which airline is handling it?

Sometimes the airline that sold your seat isn't the one flying the plane. Your itinerary shows this as "Operated by" another airline. The airline that operates the flight is the one handling the cancellation or delay. [What is a codeshare flight?](/help/booking/codeshare-flights) explains more.

## What if the airline tells me to contact the company I booked with?

That's us. Contact us the same way, and our team will take it from there.

## Did the change come from us?

If you said yes to the Free Flight Upgrade question at checkout, we may move you to a faster route on the same dates, in the same cabin, at no extra cost. We always email you when we do. [The Free Flight Upgrade question at checkout](/help/booking/free-flight-upgrade-question) explains how it works.`,
  },
  {
    slug: 'correct-traveler-details',
    title: 'Fixing a name or date of birth on your booking',
    summary: 'Spotted a mistake? Contact us as soon as you can. The Defera Concierge sends your request to our team; include the details exactly as on the traveler’s ID.',
    keywords: ['misspelled', 'typo', 'wrong birthday', 'DOB', 'legal name', 'government ID', 'passport', 'correction'],
    related: ['booking/traveler-information', 'confirmation-and-travel/travel-documents', 'account/saved-passengers', 'changes-cancellations/remove-a-traveler'],
    updated: '2026-10-23',
    body: `If a traveler's name is misspelled, or a name or date of birth on your booking doesn't match their government ID, contact us as soon as you notice. The Defera Concierge, our AI concierge, sends your request to our team, and the answer appears on your trip page and arrives by email.

It's worth raising early. The names on a booking need to match each traveler's government ID, which they'll show when they travel. Mistakes happen, and the sooner our team knows, the better.

## How do I ask?

Here's what happens:

1. **Start a chat.** Open a chat with the [Defera Concierge](/support).
2. **Say what needs fixing.** Tell it which trip and which traveler, and what needs correcting.
3. **The concierge sends your request.** It goes to our team and shows as a card on your trip page, marked "Submitted · in progress".
4. **Our team replies.** The answer appears on that card and arrives by email, usually within 24 hours.

If you'd like a different person to travel instead, ask the same way.

## Can I call or email instead?

Yes. Call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}) to reach the same concierge by voice, 24/7. You can also send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.

## What should I include?

- **Your booking reference.** It's the code at the top of your booking confirmation email, and it's on your trip page too.
- **The traveler's details as they appear on the booking now.**
- **The correct name or date of birth,** exactly as it appears on the traveler's government ID, or on their passport for an international trip.

For example, if the booking says "Jon Smith" but the passport says "Jonathan Smith", send us both, written exactly as shown.

## Where can I check the details on my booking?

- **Your booking confirmation email** lists every traveler detail entered at checkout. Passport and ID numbers show only their last 4 digits.
- **Your trip page** shows the full details. Open **Trips**, tap the trip, and look under **Passengers**.

## What happens next?

The request shows on your trip page while our team works on it, and the answer appears there and arrives by email. We can't say in advance what's possible for a particular booking, so please don't rely on a correction until our team has replied.

## How do I stop it happening again?

Saved travelers fill in your future bookings automatically. If the mistake came from a saved traveler, fix it there too. Open your avatar menu, choose **Profile**, and under **Passengers**, tap the edit icon on that traveler's row.`,
  },
];
